import { useCallback, useEffect, useRef, useState } from "react"
import { cardTint } from "@/components/CardFace/cardTint"
import { cardNumber } from "@/content/cards"
import { MOSCOW_BUCKETS } from "@/types"
import type { CardId, MoscowBucket } from "@/types"
import styles from "@/styles/pages/Priorities.module.css"

/*
 * Carrying a card to a quadrant — from the focus slot, and from whichever
 * quadrant it is already in.
 *
 * Pointer events rather than HTML5 drag and drop, and that is the whole reason
 * this file exists. Native `draggable` refiles a card from the focus slot in
 * Chrome and nowhere else that matters: Firefox and Safari will not start a
 * drag on a `<button>` at all, which is exactly what a filed card is, and no
 * browser gives it a touch story — so a facilitator on an iPad could not move a
 * card between quadrants once it had landed in one. Pointer events are one code
 * path for mouse, pen and touch.
 *
 * Click-to-place is still the path this is layered over, not replaced: the
 * quadrant is a button, the keyboard reaches the board through it, and a filed
 * card can still be clicked back into the focus slot. This adds the gesture the
 * board looks like it should have.
 *
 * The gesture is exempt from the spinner rule for the usual reason — the card
 * following the pointer IS the feedback — but the write it ends in is not: the
 * quadrant it lands in spins like any other placement.
 */

/*
 * How far the pointer travels before a press becomes a drag. Under it the press
 * is a click, which is what keeps the send-back-to-the-slot click on a filed
 * card working: the same button is both the handle and the control.
 */
const THRESHOLD = 6

interface BoardDragOptions {
  /** The facilitator is driving. Nobody else moves cards; see `Priorities`. */
  enabled: boolean
  /** Called once, on release over a quadrant. Never for a press that did not move. */
  onDrop: (cardId: CardId, bucket: MoscowBucket) => void
}

interface Press {
  cardId: CardId
  pointerId: number
  /** Where the press started, for the threshold. */
  x: number
  y: number
  /** Past the threshold: this is a drag, and a ghost is on the page. */
  moving: boolean
  over: MoscowBucket | null
  ghost: HTMLElement | null
}

export interface BoardDrag {
  /** The card under the pointer, so its tile can be ghosted where it came from. */
  carrying: CardId | null
  /** The quadrant it would land in. */
  over: MoscowBucket | null
  /** `onPointerDown` for a grab handle. */
  grab: (cardId: CardId) => (event: React.PointerEvent) => void
  /**
   * `onClickCapture` for whatever wraps a handle. A drag that ends back over
   * the card it started on still fires a click, and on a filed card that click
   * would pull it out of the board it was just moved inside of.
   */
  swallowClick: (event: React.MouseEvent) => void
}

export function useBoardDrag({ enabled, onDrop }: BoardDragOptions): BoardDrag {
  const [carrying, setCarrying] = useState<CardId | null>(null)
  const [over, setOver] = useState<MoscowBucket | null>(null)

  /*
   * Everything a move or a release reads is in a ref, not in state.
   *
   * Same reason `SwipeDeal` keeps its drag in one: a flick can deliver its last
   * `pointermove` and the `pointerup` in a single task, so the drop has to read
   * the quadrant the move just computed rather than the one React has committed.
   * `carrying` and `over` are state only because they are what the board paints,
   * and they change once per drag and once per boundary crossed — not per frame.
   */
  const press = useRef<Press | null>(null)
  const detach = useRef<(() => void) | null>(null)
  /** True for the one click the browser fires at the end of a drag. */
  const dragged = useRef(false)

  const end = useCallback(() => {
    press.current?.ghost?.remove()
    press.current = null
    detach.current?.()
    detach.current = null
    document.body.style.userSelect = ""
    setCarrying(null)
    setOver(null)
  }, [])

  /* A drag still running when the round moves on: the listeners are on `window`
     and the ghost is parked on `body`, so nothing else would take either off. */
  useEffect(() => end, [end])

  const grab = (cardId: CardId) => (event: React.PointerEvent) => {
    if (!enabled) return
    // A right or middle press is not a grab.
    if (event.button !== 0) return
    /* Nor is a second finger, which arrives with `button === 0` like any other
       touch — `isPrimary` is the only thing that tells the two apart. Rejected
       before the stale-press check below, or the second finger would end the
       drag the first one is still running and take the card over. */
    if (!event.isPrimary) return
    /* A press left standing: the pointer was released outside the window, where
       there is no `pointerup` to hear. Dropped rather than refused, or the board
       would be un-draggable until the round changed. */
    if (press.current) end()
    /*
     * A handle usually has other controls beside it — the peek eye, the
     * send-back ×, the card's own flip — and some of them are inside it. A
     * press that landed on a button that is not the handle itself belongs to
     * that button.
     */
    const button = (event.target as Element).closest("button")
    if (button && button !== event.currentTarget) return

    press.current = {
      cardId,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      moving: false,
      over: null,
      ghost: null,
    }

    const move = (moveEvent: PointerEvent) => {
      const state = press.current
      if (!state || moveEvent.pointerId !== state.pointerId) return

      if (!state.moving) {
        if (Math.hypot(moveEvent.clientX - state.x, moveEvent.clientY - state.y) < THRESHOLD) {
          return
        }
        state.moving = true
        state.ghost = makeGhost(state.cardId)
        document.body.appendChild(state.ghost)
        // Dragging across a board of headings and hints selects them otherwise,
        // and the selection outlives the drop.
        document.body.style.userSelect = "none"
        setCarrying(state.cardId)
      }

      state.ghost?.style.setProperty(
        "transform",
        `translate3d(${moveEvent.clientX}px, ${moveEvent.clientY}px, 0) translate(-50%, -50%) rotate(-4deg)`
      )

      const bucket = bucketAt(moveEvent.clientX, moveEvent.clientY)
      if (bucket === state.over) return
      state.over = bucket
      setOver(bucket)
    }

    const up = (upEvent: PointerEvent) => {
      const state = press.current
      if (!state || upEvent.pointerId !== state.pointerId) return
      // Read before `end` clears it, and taken from the move rather than
      // re-hit-tested here, so the card lands in the quadrant that was lit.
      const { moving, over: bucket } = state
      end()
      if (!moving) return
      /* The click the release is about to produce is not a click on the card.
         Cleared on the next task, which is after that click has been dispatched. */
      dragged.current = true
      window.setTimeout(() => {
        dragged.current = false
      })
      if (bucket) onDrop(cardId, bucket)
    }

    const cancel = (cancelEvent: PointerEvent) => {
      if (press.current && cancelEvent.pointerId !== press.current.pointerId) return
      end()
    }

    // Escape puts the card back, the way it closes anything else mid-gesture.
    const key = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key === "Escape") end()
    }

    /*
     * On `window`, not captured on the handle. A filed card's tile is re-rendered
     * by every snapshot the round delivers, and pointer capture dies with the
     * element it was taken on — the drag would end mid-air the moment anyone
     * else in the room wrote something.
     */
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
    window.addEventListener("pointercancel", cancel)
    window.addEventListener("keydown", key)
    detach.current = () => {
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", up)
      window.removeEventListener("pointercancel", cancel)
      window.removeEventListener("keydown", key)
    }
  }

  const swallowClick = (event: React.MouseEvent) => {
    if (!dragged.current) return
    event.preventDefault()
    event.stopPropagation()
  }

  return { carrying, over, grab, swallowClick }
}

/**
 * The quadrant under the pointer, by hit test rather than by cached rectangles:
 * the board reflows as cards fill it, and a filed card can be dropped into the
 * quadrant it is already in, which no rectangle read at drag start would know.
 * The ghost is `pointer-events: none`, so it never answers this itself.
 */
function bucketAt(x: number, y: number): MoscowBucket | null {
  const quad = document.elementFromPoint(x, y)?.closest<HTMLElement>("[data-bucket]")
  const value = quad?.dataset.bucket
  return MOSCOW_BUCKETS.find((bucket) => bucket === value) ?? null
}

/**
 * The card being carried: a `CardThumb` in everything but implementation.
 *
 * Built by hand and parked on `body` for the same reasons the landing flight in
 * `useCardLanding` is — it has to cross the quadrants and whatever clips them,
 * and it moves every frame, which as React state would re-render the whole
 * board sixty times a second to move one 56px tile.
 */
function makeGhost(cardId: CardId): HTMLElement {
  const { tint, ink, family } = cardTint(cardId)

  const ghost = document.createElement("div")
  ghost.className = styles.ghost
  ghost.dataset.family = String(family)
  ghost.setAttribute("aria-hidden", "true")
  ghost.inert = true
  ghost.style.background = tint
  ghost.style.color = ink
  ghost.textContent = cardNumber(cardId)
  return ghost
}
